import { SxProps, Theme, useMediaQuery, useTheme } from "@mui/material";
import {
  DataGridPremiumProps,
  GridCallbackDetails,
  GridColumnVisibilityModel,
  GridEventListener,
  GridRowHeightParams,
  GridSlotsComponent,
  GridValidRowModel,
  GridApi,
  GridRowParams,
  GridRowId,
  GridSortModel,
  GridInitialState,
  MuiEvent,
} from "@mui/x-data-grid-premium";
import { DataGrid, DataGridProps } from "components/DataGrid";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useGridSelection } from "hooks/useGridSelection";
import {
  OmnisearchGridColDef,
  useOmnisearchDatagrid,
} from "hooks/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { NoRowsOverlay } from "routes/Customer/Chemicals/Inventory/Facility/NoRowsOverlay";
import {
  OmnisearchColumnFilterMenu,
  OmnisearchColumnPopover,
} from "./OmnisearchColumnPopover";
import {
  DataDisplayProps,
  OmnisearchDataDisplay,
  OmnisearchDataDisplayProps,
} from "./OmnisearchDataDisplay";
import { OmnisearchErrorDisplay } from "./OmnisearchErrorDisplay";
import { CommandButton } from "./CommandButton";

export type GridApiRef = React.RefObject<GridApi | null>;
type DataGridRef = React.RefObject<HTMLDivElement | null>;

export type OmnisearchDataGridProps<
  TData,
  TVariables,
  TRow extends GridValidRowModel
> = Omit<OmnisearchDataDisplayProps<TData, TVariables, TRow>, "dataDisplay"> & {
  apiRef?: GridApiRef;
  dataGridRef?: DataGridRef;
  gridSx?: SxProps<Theme>;
  slots?: Partial<GridSlotsComponent>;
  columns: OmnisearchGridColDef<TRow>[];
  noDataMessage?: string | string[];
  /** Button text to display in the grid when there is no data for the default search (must be defined to display the button) */
  noDataButtonText?: string;
  rowHeight?: number | "auto";
  getRowHeight?: (params: GridRowHeightParams) => number | "auto";
  onColumnWidthChange?: GridEventListener<"columnWidthChange">;
  pageSizeOptions?: DataGridProps<TRow>["pageSizeOptions"];
  hideFooter?: DataGridProps<TRow>["hideFooter"];
  autosizeOptions?: DataGridPremiumProps<TRow>["autosizeOptions"];
  /** When true, the omnisearch max width will always be 100%. `commandButtons`
   * may still be used */
  showManageColumns?: boolean;
  columnVisibilityModel?: GridColumnVisibilityModel;
  onColumnVisibilityModelChange?: (
    model: GridColumnVisibilityModel,
    details: GridCallbackDetails<any>
  ) => void;
  getDetailPanelContent?: (params: GridRowParams<TRow>) => React.ReactNode;
  getDetailPanelHeight?: (params: GridRowParams<TRow>) => "auto" | number;
  getRowId?: (row: TRow) => GridRowId;
  disableRowSelectionOnClick?: boolean;
  checkboxSelection?: boolean;
  /** When true, hides the "select all" checkbox in the header while keeping individual row checkboxes */
  disableSelectAll?: boolean;
  isRowSelectable?: (params: GridRowParams<TRow>) => boolean;
  /**
   * Handler called when selection changed with row data.
   *
   * **Cross-page support**: This handler DOES track selections across multiple pages.
   * Selected rows from other pages are preserved when navigating between pages.
   *
   * **Important considerations**:
   * - Row data for items on other pages will be cached/stale (not refreshed)
   * - When pre-selecting rows via `selectedIds` that aren't on the current page,
   *   those rows won't appear in the callback until you navigate to their page
   * - For current page items, you always get fresh/live object references
   *
   * **When to use this handler**:
   * - Bulk selection features where you need row data
   * - Use cases that don't require initial off-page row selection
   * - When cached data for off-page items is acceptable
   *
   * **When to use `onSelectedIdsChanged` instead**:
   * - When you need to pre-select rows that may be off-page initially
   * - When you need guaranteed fresh data for all selected items
   * - When you want to fetch row data separately based on IDs
   */
  onSelectedRowsChanged?: (rows: TRow[]) => void;
  /** Handler called when selection changed. This can include IDs across pages
   * */
  onSelectedIdsChanged?: (ids: GridRowId[]) => void;
  /** Set grid's selected IDs. This can include IDs across pages. */
  selectedIds?: GridRowId[];
  initialSortModel?: GridSortModel;
  initialState?: GridInitialState;
  /**
   * Function to handle the datagrid's onRowClick event.
   * i.e.
   * ```
   * const handleRowClick = (params: GridRowParams) => {
   *  history.push(`/facility/${params.id}`);
   * };
   */
  onRowClick?: (
    params: GridRowParams<TRow>,
    event: MuiEvent<React.MouseEvent<HTMLElement, MouseEvent>>,
    details: GridCallbackDetails
  ) => void;
};
export function OmnisearchDataGrid<
  TData,
  TVariables,
  TRow extends GridValidRowModel
>(props: OmnisearchDataGridProps<TData, TVariables, TRow>) {
  const {
    apiRef,
    columns: columnsInput,
    showManageColumns,
    persistenceKey,
    additionalFilterColumns,
    excludeFilterColumns,
    isURLDriven,
    initialSortModel,
    initialPageSize,
    initialFilters,
    columnVisibilityModel: columnVisibilityModelProp,
    onColumnVisibilityModelChange,
  } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    columns,
    setColumnVisibilityModel,
    columnVisibilityModel,
    unsetColumnWidthModel,
  } = useOmnisearchDatagrid({
    persistenceKey,
    columns: columnsInput,
    additionalFilters: additionalFilterColumns,
    excludeFilters: excludeFilterColumns,
    isURLDriven,
    initialSortModel,
    initialPageSize,
    initialFilters,
    initialColumnVisibilityModel: columnVisibilityModelProp,
    isMobile,
  });

  const shouldShowManageColumns = useMemo(() => {
    if (showManageColumns === undefined)
      return columnsInput.filter((c) => c.type !== "actions").length >= 5;
    return showManageColumns;
  }, [showManageColumns, columnsInput]);

  const [columnListAnchorEl, setColumnListAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const showColumnList = Boolean(columnListAnchorEl);

  const handleColumnList = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setColumnListAnchorEl(event.currentTarget);
    },
    []
  );

  const handleCloseColumnList = useCallback(() => {
    setColumnListAnchorEl(null);
  }, []);

  const resetColumnWidth = useCallback(
    (field: string) => {
      const colDef = columnsInput.find((c) => c.field === field);

      if (!colDef) return;

      unsetColumnWidthModel(colDef.field);
    },
    [columnsInput, unsetColumnWidthModel]
  );

  const handleToggleColumn = useCallback(
    (field: string) => {
      const newModel = {
        ...columnVisibilityModel,
        [field]:
          field in columnVisibilityModel
            ? !columnVisibilityModel[field]
            : false,
      };

      if (!newModel[field]) resetColumnWidth(field);

      setColumnVisibilityModel(newModel);

      if (onColumnVisibilityModelChange && apiRef?.current)
        onColumnVisibilityModelChange(newModel, {
          reason: "columnVisibilityChanged",
          api: apiRef?.current,
        });
    },
    [
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      resetColumnWidth,
      columnVisibilityModel,
      apiRef,
    ]
  );

  return (
    <OmnisearchDataDisplay
      {...props}
      manageColumnsCommandButton={
        shouldShowManageColumns && (
          <>
            <CommandButton onClick={handleColumnList}>
              Manage Columns
            </CommandButton>
            {showColumnList && (
              <OmnisearchColumnPopover
                showColumnList={showColumnList}
                anchorEl={columnListAnchorEl}
                handleCloseColumnList={handleCloseColumnList}
                columns={columns.filter(
                  (column) =>
                    column.headerName !== "actions" &&
                    column.field !== "actions"
                )}
                handleToggleColumn={handleToggleColumn}
                selectedColumns={columnVisibilityModel}
                setSelectedColumns={(model) => {
                  setColumnVisibilityModel(model);
                }}
              />
            )}
          </>
        )
      }
      dataDisplay={(dataDisplayProps) => (
        <HydratedOmnisearchDataGrid {...dataDisplayProps} {...props} />
      )}
    />
  );
}

export type HydratedOmnisearchDataGridProps<
  TData,
  TVariables,
  TRow extends GridValidRowModel
> = OmnisearchDataGridProps<TData, TVariables, TRow> &
  DataDisplayProps<TData, TRow>;

/**
 * This component is used to render the grid with the data already hydrated.
 * It is used to render the grid with the data already hydrated.
 */
export function HydratedOmnisearchDataGrid<
  TData,
  TVariables,
  TRow extends GridValidRowModel
>(props: HydratedOmnisearchDataGridProps<TData, TVariables, TRow>) {
  const {
    apiRef,
    dataGridRef,
    persistenceKey,
    columns: columnsInput,
    rowHeight,
    getRowHeight,
    gridSx,
    slots,
    excludeFilterColumns,
    additionalFilterColumns,
    initialFilters,
    columnVisibilityModel: columnVisibilityModelProp,
    noDataMessage = "No results found.",
    noDataButtonText,
    noDataOnButtonClick,
    onSelectedRowsChanged,
    onSelectedIdsChanged,
    isRowSelectable,
    onRowClick,
    onColumnVisibilityModelChange,
    onColumnWidthChange,
    initialSortModel,
    initialState,
    initialPageSize,
    isURLDriven = false,
    getDetailPanelContent,
    getDetailPanelHeight,
    getRowId,
    disableRowSelectionOnClick,
    checkboxSelection = false,
    disableSelectAll = false,
    getRowLink,
    selectedIds,
    pageSizeOptions,
    hideFooter,
    autosizeOptions,
    error,
    items,
    count,
    loading,
    previousData,
    paginationModel,
    setPaginationModel,
  } = props;

  const theme = useTheme();
  const { user } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    columns,
    omnisearch,
    sortModel,
    setOmnisearch,
    setColumnVisibilityModel,
    columnVisibilityModel,
    setColumnWidthModel,
    unsetColumnWidthModel,
    setSortModel,
  } = useOmnisearchDatagrid({
    persistenceKey,
    columns: columnsInput,
    additionalFilters: additionalFilterColumns,
    excludeFilters: excludeFilterColumns,
    isURLDriven,
    initialSortModel,
    initialPageSize,
    initialFilters,
    initialColumnVisibilityModel: columnVisibilityModelProp,
    isMobile,
  });

  const { rowSelectionModel, setRowSelectionModel } = useGridSelection<TRow>({
    selectedIds,
    items,
    onSelectedRowsChanged,
    onSelectedIdsChanged,
  });

  const clearOmnisearch = useCallback(() => {
    setOmnisearch("");
  }, [setOmnisearch]);

  // Replace the error throw with graceful handling
  const errorMessage = useMemo(() => {
    if (error) {
      return Array.isArray(error.message)
        ? error.message.join(". ")
        : error.message || "An error occurred while fetching data";
    }
    return null;
  }, [error]);

  const noRowsMessage = useMemo(() => {
    if (errorMessage) return errorMessage;
    return omnisearch ? "No results found." : noDataMessage;
  }, [noDataMessage, omnisearch, errorMessage]);

  const noRowsButtonText = useMemo(
    () => (omnisearch ? "Clear search" : noDataButtonText),
    [noDataButtonText, omnisearch]
  );

  const noRowsOnButtonClick = useMemo(
    () => (omnisearch ? clearOmnisearch : noDataOnButtonClick),
    [clearOmnisearch, noDataOnButtonClick, omnisearch]
  );

  const resetColumnWidth = useCallback(
    (field: string) => {
      const colDef = columnsInput.find((c) => c.field === field);

      if (!colDef) return;

      unsetColumnWidthModel(colDef.field);
    },
    [columnsInput, unsetColumnWidthModel]
  );

  const handleToggleColumn = useCallback(
    (field: string) => {
      const newModel = {
        ...columnVisibilityModel,
        [field]:
          field in columnVisibilityModel
            ? !columnVisibilityModel[field]
            : false,
      };

      if (!newModel[field]) resetColumnWidth(field);

      setColumnVisibilityModel(newModel);

      if (onColumnVisibilityModelChange && apiRef?.current)
        onColumnVisibilityModelChange(newModel, {
          reason: "columnVisibilityChanged",
          api: apiRef?.current,
        });
    },
    [
      onColumnVisibilityModelChange,
      setColumnVisibilityModel,
      resetColumnWidth,
      columnVisibilityModel,
      apiRef,
    ]
  );

  const columnVisibilityModelChanged = useCallback(
    (model: GridColumnVisibilityModel, details: GridCallbackDetails) => {
      setColumnVisibilityModel(model);

      Object.entries(model).forEach(([field, visible]) => {
        if (!visible) resetColumnWidth(field);
      });

      if (onColumnVisibilityModelChange)
        onColumnVisibilityModelChange(model, details);
    },
    [onColumnVisibilityModelChange, setColumnVisibilityModel, resetColumnWidth]
  );

  const columnWidthChanged: GridEventListener<"columnWidthChange"> =
    useCallback(
      (params, event, details) => {
        setColumnWidthModel(params);

        if (onColumnWidthChange) onColumnWidthChange(params, event, details);
      },
      [onColumnWidthChange, setColumnWidthModel]
    );

  const memoizedSlots: Partial<GridSlotsComponent> | undefined = useMemo(
    () => ({
      columnsManagement: () => (
        <OmnisearchColumnFilterMenu
          columns={columns.filter(
            (column) =>
              column.headerName !== "actions" && column.field !== "actions"
          )}
          handleToggleColumn={handleToggleColumn}
          selectedColumns={columnVisibilityModel}
          setSelectedColumns={(model) => {
            setColumnVisibilityModel(model);
          }}
        />
      ),
      noRowsOverlay: (props) =>
        error ? (
          <OmnisearchErrorDisplay
            error={error}
            onClear={() => {
              clearOmnisearch();
              setSortModel([]);
            }}
          />
        ) : (
          <NoRowsOverlay
            {...props}
            onButtonClick={noRowsOnButtonClick}
            message={noRowsMessage}
            buttonText={noRowsButtonText}
          />
        ),
      ...slots,
    }),
    [
      slots,
      noRowsOnButtonClick,
      noRowsMessage,
      noRowsButtonText,
      error,
      clearOmnisearch,
      handleToggleColumn,
      columnVisibilityModel,
      setColumnVisibilityModel,
      columns,
      setSortModel,
    ]
  );

  if (!user) return <></>;
  return (
    <DataGrid
      apiRef={apiRef}
      innerRef={dataGridRef}
      columns={columns}
      autosizeOptions={autosizeOptions}
      rows={items}
      // Defaulted to match our current designs but allows it to be overridden
      getRowHeight={getRowHeight ?? (() => rowHeight ?? 38)}
      autoHeight={rowHeight === "auto"}
      getRowId={getRowId}
      sx={{
        scrollMarginTop: 10, // Help prevent scrolling past the top of the grid
        "& .MuiDataGrid-virtualScroller": {
          minHeight: items.length === 0 || error ? "400px" : "0",
        },
        opacity:
          loading && previousData ? theme.palette.action.disabledOpacity : 1,
        ...(disableSelectAll && {
          "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer":
            {
              display: "none",
            },
        }),
        ...gridSx,
      }}
      checkboxSelection={
        !!onSelectedRowsChanged || !!onSelectedIdsChanged || checkboxSelection
      }
      disableRowSelectionOnClick={
        typeof disableRowSelectionOnClick === "boolean"
          ? disableRowSelectionOnClick
          : !(!!onSelectedRowsChanged || !!onSelectedIdsChanged)
      }
      loading={loading}
      rowCount={count}
      pagination
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={setPaginationModel}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={setSortModel}
      rowSelectionModel={rowSelectionModel}
      onRowSelectionModelChange={(newModel) => {
        setRowSelectionModel(newModel);
      }}
      isRowSelectable={
        isRowSelectable
          ? (params) => !!params.row && isRowSelectable(params)
          : undefined
      }
      onColumnVisibilityModelChange={columnVisibilityModelChanged}
      onColumnWidthChange={columnWidthChanged}
      keepNonExistentRowsSelected
      slots={memoizedSlots}
      onRowClick={onRowClick}
      columnVisibilityModel={columnVisibilityModel}
      initialState={initialState}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      hideFooterPagination={false}
      hideFooter={hideFooter}
      getRowLink={getRowLink}
      pageSizeOptions={pageSizeOptions}
    />
  );
}
